import { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';

import {
  fetchRezeptInvoiceList,
  type RezeptInvoiceListFilters,
} from '@/services/main/rezept';
import useAppToast from '@/hooks/useAppToast';
import { getApiErrorMessage, DEFAULT_FETCH_ERROR_MESSAGE } from '@/utils/apiError';
import { useClinicContext } from './useClinicContext';
import { useTheme } from '@shared/providers/theme/ThemeContext';

/**
 * Paginated hook for the 領収書・請求書 (invoice list + search) screen.
 * Fetches only once `tenant`, `clinicNumber`, and `filters` are all set.
 */
export const useRezeptInvoiceList = (
  filters?: RezeptInvoiceListFilters,
  options?: { enabled?: boolean }
) => {
  const { tenant, clinicNumber } = useClinicContext();
  const { serviceType } = useTheme();
  const toast = useAppToast();

  const { data, isLoading, isFetching, isError, isPending, refetch, error } = useQuery({
    queryKey: ['rezept-invoice', 'list', tenant, clinicNumber, serviceType, filters],
    queryFn: () => fetchRezeptInvoiceList(tenant, clinicNumber, filters!),
    enabled: (options?.enabled ?? true) && !!tenant && !!clinicNumber && !!filters,
    staleTime: 30_000,
    // Keep previous rows during pagination/filter changes, but drop stale rows
    // when the user switches service type (A2 ↔ R2).
    placeholderData: (prev, prevQuery) => {
      if (!prev || !prevQuery) return undefined;
      const prevServiceType = (prevQuery.queryKey as unknown[])[4];
      return prevServiceType === serviceType ? prev : undefined;
    },
  });

  useEffect(() => {
    if (isError && error) {
      toast.error(getApiErrorMessage(error, DEFAULT_FETCH_ERROR_MESSAGE));
    }
  }, [isError, error, toast]);

  return { data, isLoading, isFetching, isError, isPending, refetch, error };
};

export default useRezeptInvoiceList;
